import { useRef, useCallback } from 'react';
import { api } from '../services/api';
import { getFieldTag } from '../utils/stageConfig';
import type { Word } from '../types/word';
import type { DiffEntry } from '../types/learning';

/**
 * Learning Layer — silent revision pair capture.
 * Tracks original AI-generated content and captures diffs when user edits cards.
 * Zero UI interruption — completely invisible to the workflow.
 */
export default function useLearningCapture(word: Word | null, stage: string): {
  recordOriginal: (field: string, value: string) => void;
  recordOriginals: (fields: Record<string, string>) => void;
  captureEdit: (field: string, newValue: string) => void;
  captureGold: (currentFields: Record<string, unknown>) => void;
  resetOriginals: () => void;
} {
  // Store originals when content is first generated
  const originalsRef = useRef<Record<string, string>>({});

  // Record the original AI-generated value for a field
  const recordOriginal = useCallback((field: string, value: string) => {
    if (!originalsRef.current[field] && value) {
      originalsRef.current[field] = value;
    }
  }, []);

  // Record originals for a batch of fields (after generation)
  const recordOriginals = useCallback((fields: Record<string, string>) => {
    for (const [field, value] of Object.entries(fields)) {
      if (value && !originalsRef.current[field]) {
        originalsRef.current[field] = value;
      }
    }
  }, []);

  const captureEdit = useCallback((field: string, newValue: string) => {
    const original = originalsRef.current[field];
    if (!original || !newValue || original.trim() === newValue.trim()) return;

    const editRatio = computeEditRatio(original, newValue);
    const diff = computeWordDiff(original, newValue);

    api.post('/api/learning/revisions', {
      word: word?.word || '',
      stage: stage || '',
      field,
      original,
      revised: newValue,
      diff,
      edit_ratio: editRatio,
      tag: getFieldTag(field),
    }).catch((err) => {
      console.debug('Learning capture failed:', err.message);
    });
  }, [word, stage]);

  // Approved-gold capture: fields approved AS-IS (current value still equals the
  // AI-generated original — the author didn't edit them) are exemplars of what
  // was good. Post them to the gold store so the prompt-improvement loop learns
  // from wins, not only from fixes. Silent, zero UI, like captureEdit.
  const captureGold = useCallback((currentFields: Record<string, unknown>) => {
    // Scope to the fields being approved right now (2026-07-05 M21 fix).
    // originalsRef accumulates every stage's originals across a word, so the
    // old "iterate the whole ref" loop re-posted every prior stage's exemplars
    // on each approval — a duplicate gold entry per field per approval. Only
    // consider the fields present in currentFields, and delete each posted
    // original from the ref so a later approval can't re-post it.
    for (const field of Object.keys(currentFields)) {
      const original = originalsRef.current[field];
      if (!original) continue;
      const current = currentFields[field];
      if (typeof current !== 'string') continue;
      // Unedited == approved gold. Edited fields are already captured by captureEdit.
      if (current.trim() !== original.trim()) {
        // Edited away from the original: drop it so it isn't reconsidered.
        delete originalsRef.current[field];
        continue;
      }
      api.post('/api/learning/gold', {
        word: word?.word || '',
        stage: stage || '',
        field,
        value: original,
        tag: getFieldTag(field),
      }).catch((err) => {
        console.debug('Gold capture failed:', err.message);
      });
      // Posted — remove so subsequent approvals don't re-post it.
      delete originalsRef.current[field];
    }
  }, [word, stage]);

  // Reset originals (e.g., when switching words)
  const resetOriginals = useCallback(() => {
    originalsRef.current = {};
  }, []);

  return {
    recordOriginal,
    recordOriginals,
    captureEdit,
    captureGold,
    resetOriginals,
  };
}

function computeEditRatio(a: string, b: string): number {
  if (!a && !b) return 0;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 0;
  const dist = levenshtein(a, b);
  return Math.round((dist / maxLen) * 100) / 100;
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev: number[] = Array.from({ length: n + 1 }, (_, i) => i);
  let curr: number[] = new Array(n + 1);
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      curr[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

function computeWordDiff(original: string, revised: string): DiffEntry[] {
  const wordsA = original.split(/(\s+)/);
  const wordsB = revised.split(/(\s+)/);
  const lcs = wordLCS(wordsA, wordsB);

  const diff: DiffEntry[] = [];
  let ai = 0, bi = 0, li = 0;
  let pos = 0;

  while (ai < wordsA.length || bi < wordsB.length) {
    if (li < lcs.length && ai < wordsA.length && wordsA[ai] === lcs[li] && bi < wordsB.length && wordsB[bi] === lcs[li]) {
      pos += wordsA[ai].length;
      ai++;
      bi++;
      li++;
    } else if (li < lcs.length && bi < wordsB.length && wordsB[bi] === lcs[li] && (ai >= wordsA.length || wordsA[ai] !== lcs[li])) {
      const deleted = [];
      while (ai < wordsA.length && (li >= lcs.length || wordsA[ai] !== lcs[li])) {
        deleted.push(wordsA[ai]);
        ai++;
      }
      if (deleted.length > 0) {
        const text = deleted.join('');
        if (text.trim()) diff.push({ type: 'delete', text, position: pos });
        pos += text.length;
      }
    } else if (li < lcs.length && ai < wordsA.length && wordsA[ai] === lcs[li] && (bi >= wordsB.length || wordsB[bi] !== lcs[li])) {
      const inserted = [];
      while (bi < wordsB.length && (li >= lcs.length || wordsB[bi] !== lcs[li])) {
        inserted.push(wordsB[bi]);
        bi++;
      }
      if (inserted.length > 0) {
        const text = inserted.join('');
        if (text.trim()) diff.push({ type: 'insert', text, position: pos });
      }
    } else {
      const deleted = [];
      while (ai < wordsA.length && (li >= lcs.length || wordsA[ai] !== lcs[li])) {
        deleted.push(wordsA[ai]);
        ai++;
      }
      const inserted = [];
      while (bi < wordsB.length && (li >= lcs.length || wordsB[bi] !== lcs[li])) {
        inserted.push(wordsB[bi]);
        bi++;
      }
      if (deleted.length > 0) {
        const text = deleted.join('');
        if (text.trim()) diff.push({ type: 'delete', text, position: pos });
        pos += text.length;
      }
      if (inserted.length > 0) {
        const text = inserted.join('');
        if (text.trim()) diff.push({ type: 'insert', text, position: pos });
      }
    }
  }

  return diff;
}

function wordLCS(a: string[], b: string[]): string[] {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const result = [];
  let i = m, j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  return result;
}
